<script setup>
/**
 * @description 官网-下载中心页面
 * @reason 还原 Figma 设计稿中的下载中心页面 (Node: 10000-31810)
 */

// 1. Imports
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { BaseMessage } from '~/utils/baseMessage'
import { useSeo } from '@/composables/useSeo'
import { getAbsoluteUrl, getOssUrl, formatDate, dateToTimestamp } from '@/utils'
import { isOssSignedUrlExpiring } from '@/utils/ossSignedUrl'
import { openResolvedPreview } from '@/utils/contentPreviewAction'
import {
  downloadFileByUrl,
  formatFileSize,
  getFileIconPath,
  getFileOpenUrl,
  isVideoFile,
  resolveDownloadDisplayName,
  resolveDownloadFileName,
} from '@/utils/download'
import { websiteApi } from '@/api/website'
import { baseApi } from '@/api/base'
import BaseFilter from '~/components/base/Filter.vue'
import BaseSimplePagination from '~/components/base/SimplePagination.vue'
import ServiceListPageLayout from '~/components/website/service/ServiceListPageLayout.vue'
import ContentListHeader from '~/components/website/service/ContentListHeader.vue'
import BaseDownloadFileItem from '~/components/base/DownloadFileItem.vue'
import { useServiceContentSuggestions } from '~/composables/useServiceContentSuggestions'
import { filterServiceFacetGroups } from '~/utils/website/serviceContentFacets'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { THEME_CSS_VARS } from '~/utils/theme'
import {
  highlightServiceSearchText,
  normalizeServiceSearchKeyword,
} from '~/utils/website/serviceContentSearch'
import { CONCURRENCY_KEYS, isCancelForConcurrency } from '~/api/concurrency'
import { createPublicContentSsrRequestConfig } from '~/utils/website/ssrRequestPolicy'
import { usePageTracking, trackClickEvent } from '~/composables/usePageTracking'
import { PAGE_TRACKING } from '~/utils/tracking/pageRegistry'
import { buildContentFileClickPayload } from '~/utils/tracking/eventBuilders'

// 2. SEO
const { setSeo } = useSeo()
setSeo({
  title: '下载中心',
  description: 'Tenda 下载中心为您提供产品配置指南、升级软件、安装视频等资料下载。',
})

definePageMeta({
  layout: 'website',
})

usePageTracking(PAGE_TRACKING.downloadCenter)

const { withBase, openExternal } = useBasePath()
const { isBelowDesktop } = useMediaQuery()
const isMobile = isBelowDesktop
const route = useRoute()
const router = useRouter()
// 3. State
const searchQuery = ref('')
const submittedSearchQuery = ref('')
const showMobileFilter = ref(false)
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)
const loading = ref(true)
const listError = ref(false)
const downloadList = ref([])
const downloadingRecordMap = ref({})
const downloadListRequestId = ref(0)
const skipNextSuggestionSchedule = ref(false)
const {
  suggestions: searchSuggestions,
  suggestionsLoading: searchSuggestionsLoading,
  suggestionsError: searchSuggestionsError,
  scheduleSuggestions: scheduleSearchSuggestions,
  clearSuggestions: clearSearchSuggestions,
} = useServiceContentSuggestions({ centerType: 1 })

const activeFilters = ref({
  productType: [],
  stage: [], // 适用阶段
  contentType: [], // 内容类型
  scene: [], // 应用场景
})

const getContentFilters = () => ({
  prodIds: activeFilters.value.productType.filter((item) => !String(item).startsWith('group_')),
  applyStages: [...activeFilters.value.stage],
  thirdContentCategoryIds: activeFilters.value.contentType.filter(
    (item) => !String(item).startsWith('group_')
  ),
  secondSceneIds: activeFilters.value.scene.filter((item) => !String(item).startsWith('group_')),
})

const filterGroups = ref([
  {
    id: 'productType',
    title: '产品类型',
    type: 'accordion',
    options: [], // 动态加载
  },
  {
    id: 'stage',
    title: '适用阶段',
    type: 'accordion',
    options: [
      { label: '售前', value: 1 },
      { label: '售后', value: 2 },
    ],
  },
  {
    id: 'contentType',
    title: '内容类型',
    type: 'accordion',
    options: [], // 动态加载
  },
  {
    id: 'scene',
    title: '适用场景',
    type: 'accordion',
    options: [], // 动态加载
  },
])
const facetCounts = ref(null)
const visibleFilterGroups = computed(() =>
  filterServiceFacetGroups(filterGroups.value, facetCounts.value)
)

const fetchDownloadFacets = async () => {
  try {
    // 选项仅按下载中心内自身是否有内容展示，不受已选条件或搜索关键词影响。
    const response = await websiteApi.getContentFacets({
      centerType: 1,
    })
    if (!response?.product || !response?.scene || !response?.category || !response?.stage) {
      throw new Error('下载筛选计数响应格式异常')
    }
    facetCounts.value = response
  } catch (error) {
    facetCounts.value = null
    console.error('获取下载筛选计数失败:', error)
  }
}

/** 从详情返回时，是否正在从 URL 恢复状态（避免 watch 重复请求） */
const isRestoringFromQuery = ref(false)

/**
 * @description Build the persisted download-list query for detail navigation and list restore.
 * @returns {Record<string, string>}
 */
const buildDownloadListQuery = () => {
  const query = {}
  if (currentPage.value > 1) query.page = String(currentPage.value)
  const keyword = normalizeServiceSearchKeyword(submittedSearchQuery.value)
  if (keyword) query.keyword = keyword
  if (activeFilters.value.productType?.length) {
    query.productType = activeFilters.value.productType.join(',')
  }
  if (activeFilters.value.stage?.length) {
    query.stage = activeFilters.value.stage.join(',')
  }
  if (activeFilters.value.contentType?.length) {
    query.contentType = activeFilters.value.contentType.join(',')
  }
  if (activeFilters.value.scene?.length) {
    query.scene = activeFilters.value.scene.join(',')
  }
  return query
}

/**
 * @description 从 URL query 恢复分页、搜索、筛选状态
 */
const syncQueryToState = () => {
  const query = route.query

  if (query.page) {
    const p = parseInt(query.page, 10)
    if (p >= 1) currentPage.value = p
  }
  if (typeof query.keyword === 'string') {
    const keyword = normalizeServiceSearchKeyword(query.keyword)
    searchQuery.value = keyword
    submittedSearchQuery.value = keyword
  }
  if (query.productType) {
    const val = query.productType
    const arr = Array.isArray(val) ? val : String(val).split(',')
    activeFilters.value.productType = arr
      .map((v) => (String(v).startsWith('group_') ? v : Number(v)))
      .filter(Boolean)
  }
  if (query.stage) {
    const val = query.stage
    activeFilters.value.stage = Array.isArray(val)
      ? val.map(Number)
      : String(val).split(',').map(Number).filter(Boolean)
  }
  if (query.contentType) {
    const val = query.contentType
    const arr = Array.isArray(val) ? val : String(val).split(',')
    activeFilters.value.contentType = arr
      .map((v) => (String(v).startsWith('group_') ? v : Number(v)))
      .filter(Boolean)
  }
  if (query.scene) {
    const val = query.scene
    activeFilters.value.scene = Array.isArray(val)
      ? val.filter(Boolean)
      : String(val).split(',').filter(Boolean)
  }
}

/**
 * @description 将分页、搜索、筛选状态同步到 URL（返回时能恢复）
 */
const syncStateToQuery = () => {
  if (!import.meta.client) return
  const query = buildDownloadListQuery()
  const resolved = router.resolve({
    path: route.path,
    query: Object.keys(query).length ? query : undefined,
  })
  window.history.replaceState(window.history.state, '', resolved.href)
}

/**
 * @description 转换场景树为 BaseFilter accordion 选项格式
 * 场景节点: { id (string), name, children, grade, parentId, ... }
 * 叶子节点(无children): raw id → selectable leaf
 * 非叶子节点: group_ prefix → accordion parent
 * @param {Array} nodes - 场景树根节点数组
 * @returns {Array<{ label: string, value: string, children: Array }>}
 */
const transformSceneTree = (nodes) => {
  if (!Array.isArray(nodes)) return []

  const mapNode = (node) => {
    const children = Array.isArray(node.children) ? node.children : []
    if (children.length === 0) {
      return {
        label: node.name,
        value: node.id,
        children: [],
      }
    }
    return {
      label: node.name,
      value: `group_${node.id}`,
      children: children.map((child) => mapNode(child)),
    }
  }

  return nodes.map((node) => mapNode(node))
}

/**
 * @description 获取分类信息并更新筛选组
 */
const fetchFilters = async (requestConfig = {}, { preserveExisting = false } = {}) => {
  try {
    const [productCategories, contentTreeRes, sceneTree] = await Promise.all([
      baseApi.getCategoryProdTree(requestConfig).catch(() => (preserveExisting ? null : [])),
      websiteApi
        .getContentCategoryTree({ rootNameContains: '下载中心' }, requestConfig)
        .catch(() => (preserveExisting ? null : [])),
      websiteApi
        .getSceneTree({ centerType: 'DOWNLOAD' }, requestConfig)
        .catch(() => (preserveExisting ? null : [])),
    ])

    // 更新产品类型筛选
    if (Array.isArray(productCategories)) {
      const getDisplayName = (node) => {
        if (node.categoryName) return node.categoryName
        if (node.prodName) {
          return node.modelName ? `${node.modelName} ${node.prodName}` : node.prodName
        }
        return node.modelName || ''
      }

      const collectLeafNodes = (node) => {
        const children = Array.isArray(node.children) ? node.children : []
        if (children.length === 0) {
          return [node]
        }
        return children.flatMap((child) => collectLeafNodes(child))
      }

      const mapLeaf = (node) => ({
        label: getDisplayName(node),
        value: node.prodId || node.categoryId,
        children: [],
      })

      const mapSecondLevel = (node) => {
        const children = Array.isArray(node.children) ? node.children : []
        const leafNodes = children.length > 0 ? collectLeafNodes(node) : []
        return {
          label: node.categoryName,
          value: `group_${node.categoryId}`,
          children: leafNodes.length > 0 ? leafNodes.map(mapLeaf) : [],
        }
      }

      const mapFirstLevel = (node) => ({
        label: node.categoryName,
        value: `group_${node.categoryId}`,
        children: Array.isArray(node.children)
          ? node.children.map((child) => mapSecondLevel(child))
          : [],
      })

      const productOptions = productCategories.map(mapFirstLevel)
      const group = filterGroups.value.find((g) => g.id === 'productType')
      if (group) group.options = productOptions
    }

    /**
     * @description 转换内容分类：跳过一级，以二级为手风琴分组、三级为可选项（与产品类型筛选结构一致）
     * @param {Array} cats - 内容分类树（一级根 → 二级 → 三级）
     * @returns {Array<{ label: string, value: string|number, children: Array }>}
     */
    const transformContentCategory = (cats) => {
      if (!Array.isArray(cats)) return []
      const result = []
      cats.forEach((lvl1) => {
        if (!Array.isArray(lvl1.children)) return
        lvl1.children.forEach((lvl2) => {
          const lvl2Id = lvl2.contentCategoryId ?? lvl2.categoryId
          const lvl3List = Array.isArray(lvl2.children) ? lvl2.children : []
          const children = lvl3List.map((lvl3) => ({
            label: lvl3.categoryName,
            value: lvl3.contentCategoryId ?? lvl3.categoryId,
            children: [],
          }))
          if (children.length > 0) {
            result.push({
              label: lvl2.categoryName,
              value: `group_${lvl2Id}`,
              children,
            })
          }
        })
      })
      return result
    }

    // 更新内容类型筛选
    if (Array.isArray(contentTreeRes)) {
      const contentOptions = transformContentCategory(contentTreeRes)
      const group = filterGroups.value.find((g) => g.id === 'contentType')
      if (group) group.options = contentOptions
    }

    // 更新场景筛选
    if (Array.isArray(sceneTree)) {
      const sceneOptions = transformSceneTree(sceneTree)
      const group = filterGroups.value.find((g) => g.id === 'scene')
      if (group) group.options = sceneOptions
    }
  } catch (error) {
    console.error('获取筛选条件失败:', error)
  }
}

/**
 * @description 获取下载内容列表
 */
const fetchDownloadList = async (requestConfig = {}, { preserveExisting = false } = {}) => {
  const requestId = downloadListRequestId.value + 1
  downloadListRequestId.value = requestId
  loading.value = true
  listError.value = false
  try {
    const params = {
      current: currentPage.value,
      size: pageSize.value,
      centerType: 1, // 下载中心
      keyword: submittedSearchQuery.value || undefined,
      ...getContentFilters(),
    }

    const response = await websiteApi.getContentList(params, {
      concurrencyKey: CONCURRENCY_KEYS.DOWNLOAD_CONTENT_LIST,
      ...requestConfig,
    })
    if (requestId !== downloadListRequestId.value) return

    const data = response || {}

    downloadList.value = Array.isArray(data.records) ? data.records : []
    downloadList.value = downloadList.value.map((item) => {
      return {
        ...item,
        displayName: resolveDownloadDisplayName(item),
        downloadUrl: getOssUrl(item.fileUrl),
      }
    })
    total.value = Number(data.total) || 0
    loading.value = false
  } catch (error) {
    if (
      isCancelForConcurrency(error, CONCURRENCY_KEYS.DOWNLOAD_CONTENT_LIST) ||
      requestId !== downloadListRequestId.value
    ) {
      return
    }
    console.error('获取下载列表失败:', error)
    listError.value = true
    if (!preserveExisting) {
      downloadList.value = []
      total.value = 0
    }
    loading.value = false
  }
}

// 4. Computed
const totalPages = computed(() => {
  return Math.ceil(total.value / pageSize.value) || 1
})

const hasActiveFilter = computed(() => {
  return Object.values(activeFilters.value).some(
    (value) => Array.isArray(value) && value.length > 0
  )
})

/**
 * @description 生成下载记录的稳定键值，用于列表按钮 loading 状态管理
 * @param {Object} item - 下载列表项
 * @returns {string}
 */
const getDownloadRecordKey = (item) => {
  return String(item?.contentId || item?.fileUrl || item?.downloadUrl || item?.title || '')
}

/**
 * @description 判断当前列表项是否处于下载中
 * @param {Object} item - 下载列表项
 * @returns {boolean}
 */
const isItemDownloading = (item) => {
  return Boolean(downloadingRecordMap.value[getDownloadRecordKey(item)])
}

const formatDownloadTime = (item) => {
  const time = item?.updateTime || item?.createTime
  if (!time) return ''

  const timestamp = dateToTimestamp(time)
  if (timestamp) {
    return formatDate(timestamp, 'YYYY-MM-DD')
  }

  const fallbackDate = String(time).trim().split(/[T\s]/)[0]
  return fallbackDate ? fallbackDate.replace(/\//g, '-') : ''
}

// 5. Watchers
// 筛选条件改变时，重置页码到第一页并重新请求；搜索输入只触发联想，下方列表等点击查询后刷新。
watch(
  activeFilters,
  () => {
    if (isRestoringFromQuery.value) return
    currentPage.value = 1
    syncStateToQuery()
    fetchDownloadList()
    if (searchQuery.value.trim()) scheduleSearchSuggestions(searchQuery.value, getContentFilters())
  },
  { deep: true }
)

watch(searchQuery, (value) => {
  if (isRestoringFromQuery.value) return
  if (skipNextSuggestionSchedule.value) {
    skipNextSuggestionSchedule.value = false
    return
  }
  const keyword = normalizeServiceSearchKeyword(value)
  if (!keyword) {
    clearSearchSuggestions()
    return
  }
  scheduleSearchSuggestions(keyword, getContentFilters())
})

// 分页变化时同步 URL
watch(currentPage, () => {
  if (isRestoringFromQuery.value) return
  syncStateToQuery()
})

// 6. Methods
const handleSearch = (keyword) => {
  clearSearchSuggestions()
  if (typeof keyword === 'string') {
    const nextKeyword = normalizeServiceSearchKeyword(keyword)
    if (searchQuery.value !== nextKeyword) {
      skipNextSuggestionSchedule.value = true
    }
    searchQuery.value = nextKeyword
  }
  submittedSearchQuery.value = normalizeServiceSearchKeyword(searchQuery.value)
  searchQuery.value = submittedSearchQuery.value
  currentPage.value = 1
  syncStateToQuery()
  fetchDownloadList()
}

const handleSearchFocus = (keyword = searchQuery.value) => {
  const searchText = normalizeServiceSearchKeyword(keyword)
  if (!searchText) return
  scheduleSearchSuggestions(searchText, getContentFilters())
}

const retrySearchSuggestions = () => {
  const keyword = normalizeServiceSearchKeyword(searchQuery.value)
  if (keyword) scheduleSearchSuggestions(keyword, getContentFilters())
}

const handleSuggestionSelect = (item) => {
  clearSearchSuggestions()
  const contentId = String(item?.contentId || '').trim()
  if (!import.meta.client || !contentId) return

  const query = buildDownloadListQuery()
  const resolved = router.resolve({
    path: `/website/service/download/${encodeURIComponent(contentId)}`,
    query: Object.keys(query).length ? query : undefined,
  })
  openExternal(resolved.href)
}

const handlePageChange = (page) => {
  currentPage.value = page
  syncStateToQuery()
  fetchDownloadList()
  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

/**
 * @description 记录内容统计操作；分享时同步上报下载中心点击埋点
 * @param {Object} item - 内容对象
 * @param {number} type - 统计类型 1 预览量 2 下载量 3 分享量 4 有帮助 5 无帮助
 */
const handleTrackAction = async (item, type) => {
  if (Number(type) === 3) {
    const fileName = resolveDownloadDisplayName(item) || item?.fileName || item?.title || ''
    trackClickEvent(
      buildContentFileClickPayload({
        pvEvent: 'pv_DownloadCenter_click',
        pageName: '下载中心',
        fileName,
        actionType: 'share',
        extra: {
          content_id: String(item?.contentId || ''),
        },
      })
    )
  }
  try {
    await baseApi.updateContentStat({
      contentId: item.contentId,
      statType: type,
    })
    return true
  } catch (error) {
    console.error('记录操作失败:', error)
  }
}

/**
 * @description 处理下载操作，并上报文件下载量
 * @param {Object} item - 内容对象
 */
const handleDownload = async (item) => {
  if (isVideoFile(item)) return
  if (!item.downloadUrl) return
  if (isItemDownloading(item)) return

  const recordKey = getDownloadRecordKey(item)
  downloadingRecordMap.value = {
    ...downloadingRecordMap.value,
    [recordKey]: true,
  }

  try {
    const freshDetail = item.contentId ? await websiteApi.getContentDetail(item.contentId) : null
    const actionItem = freshDetail ? { ...item, ...freshDetail } : item
    const actionUrl = actionItem.fileUrl || actionItem.downloadUrl
    if (!actionUrl) throw new Error('File URL is missing')

    trackClickEvent(
      buildContentFileClickPayload({
        pvEvent: 'pv_DownloadCenter_click',
        pageName: '下载中心',
        fileName: resolveDownloadDisplayName(actionItem) || actionItem?.fileName || '',
        actionType: 'download',
      })
    )

    const result = await downloadFileByUrl({
      url: actionUrl,
      filename: resolveDownloadFileName(actionItem),
      fileType: actionItem.fileType,
      contentForm: actionItem.contentForm,
    })
    BaseMessage.success(result?.strategy === 'native' ? '已开始下载' : '保存成功')
    handleTrackAction(item, 2)

    downloadList.value = downloadList.value.map((record) => {
      if (record.contentId !== item.contentId) return record

      const nextCount = Number(record.downloadCount || record.downloadTimes || 0) + 1
      return {
        ...record,
        downloadCount: nextCount,
        downloadTimes: nextCount,
      }
    })
  } catch (error) {
    BaseMessage.error('下载失败，请稍后重试')
  } finally {
    const nextMap = { ...downloadingRecordMap.value }
    delete nextMap[recordKey]
    downloadingRecordMap.value = nextMap
  }
}

/**
 * @description 获取下载列表项的直接预览地址，未支持预览的文件返回空字符串。
 * @param {Object} item - 下载列表项。
 * @returns {string} 可在新标签页打开的预览地址。
 */
const getDownloadPreviewUrl = (item) => {
  const url = item?.downloadUrl || (item?.fileUrl ? getOssUrl(item.fileUrl) : '')
  const coverUrl = item?.coverUrl || item?.videoCover?.url || ''
  return getFileOpenUrl({
    url,
    fileName: resolveDownloadFileName(item),
    fileType: item?.fileType,
    poster: coverUrl ? getOssUrl(coverUrl) : '',
  })
}

const shouldRefreshDownloadPreview = (item) => {
  const fileUrl = item?.downloadUrl || item?.fileUrl || ''
  const coverUrl = item?.coverUrl || item?.videoCover?.previewUrl || item?.videoCover?.url || ''
  return isOssSignedUrlExpiring(fileUrl) || isOssSignedUrlExpiring(coverUrl)
}

const refreshDownloadPreviewItem = async (item) => {
  if (!item?.contentId || !shouldRefreshDownloadPreview(item)) return item
  const freshDetail = await websiteApi.getContentDetail(item.contentId)
  return freshDetail ? { ...item, ...freshDetail } : item
}

/**
 * @description 获取资料直接打开的目标地址，优先使用预览地址。
 * @param {Object} item - 下载列表项。
 * @returns {string} 预览地址或文件下载地址。
 */
const getDownloadOpenUrl = (item) => {
  return getDownloadPreviewUrl(item) || item?.downloadUrl || ''
}

/**
 * @description 获取下载资料详情路径，供列表项主体点击跳转。
 * @param {Object} item - 下载列表项。
 * @returns {string} 下载资料详情路径。
 */
const getDownloadDetailPath = (item) => {
  const contentId = String(item?.contentId || '').trim()
  if (!contentId) return ''
  return `/website/service/download/${encodeURIComponent(contentId)}`
}

/**
 * @description 点击下载列表主区域时优先预览文件，其次下载，最后进入资料详情兜底。
 * @param {Object} item - 下载列表项。
 * @returns {void}
 */
const handleViewItem = async (item) => {
  if (!import.meta.client) return

  const fileName = resolveDownloadDisplayName(item) || item?.fileName || item?.title || ''
  trackClickEvent(
    buildContentFileClickPayload({
      pvEvent: 'pv_DownloadCenter_click',
      pageName: '下载中心',
      fileName,
      actionType: 'preview',
    })
  )

  try {
    const result = await openResolvedPreview({
      source: item,
      shouldRefresh: shouldRefreshDownloadPreview(item),
      refreshSource: refreshDownloadPreviewItem,
      resolvePreviewUrl: getDownloadPreviewUrl,
      openUrl: openExternal,
    })
    if (result.opened) {
      handleTrackAction(item, 1)
      return
    }
  } catch (error) {
    console.error('refresh download preview failed:', error)
  }

  if ((item?.downloadUrl || item?.fileUrl) && !isVideoFile(item)) {
    handleDownload(item)
    return
  }

  const detailPath = getDownloadDetailPath(item)
  if (!detailPath) return

  handleTrackAction(item, 1)
  openExternal(detailPath)
}

const getShareUrl = (item) => {
  if (!import.meta.client) return ''
  const detailPath = getDownloadDetailPath(item)
  const shareTarget = detailPath ? withBase(detailPath) : getDownloadOpenUrl(item)
  return getAbsoluteUrl(shareTarget)
}

/** 高亮文本中的搜索关键词 */
const highlightText = (text) => {
  return highlightServiceSearchText(text, submittedSearchQuery.value, THEME_CSS_VARS.primary)
}

if (import.meta.server) {
  isRestoringFromQuery.value = true
  syncQueryToState()
}

const publicContentSsrEnabled = usePublicContentSsrEnabled()
const { data: initialDownloadPage } = await useAsyncData(
  () => `website-download-list:${route.fullPath}`,
  async () => {
    if (import.meta.client) return { filterGroups: [], records: [], total: 0 }

    const requestConfig = createPublicContentSsrRequestConfig()
    await fetchFilters(requestConfig)
    await fetchDownloadList(requestConfig)
    return {
      filterGroups: filterGroups.value,
      records: downloadList.value,
      total: total.value,
    }
  },
  usePublicContentAsyncDataOptions({
    default: () => ({ filterGroups: [], records: [], total: 0 }),
  })
)

if (publicContentSsrEnabled.value && (import.meta.server || useNuxtApp().isHydrating)) {
  if (
    Array.isArray(initialDownloadPage.value.filterGroups) &&
    initialDownloadPage.value.filterGroups.length > 0
  ) {
    filterGroups.value = initialDownloadPage.value.filterGroups
  }
  downloadList.value = Array.isArray(initialDownloadPage.value.records)
    ? initialDownloadPage.value.records
    : []
  total.value = Number(initialDownloadPage.value.total) || 0
  loading.value = false
}

onMounted(async () => {
  isRestoringFromQuery.value = true
  syncQueryToState()
  await Promise.all([fetchFilters({}, { preserveExisting: true }), fetchDownloadFacets()])
  await fetchDownloadList({}, { preserveExisting: true })
  isRestoringFromQuery.value = false
})
</script>

<template>
  <ServiceListPageLayout
    v-model:keyword="searchQuery"
    title="下载中心"
    :filter-active="hasActiveFilter"
    placeholder="请输入关键词搜索"
    mobile-search-type="download"
    page-class="download-center-page"
    content-class="download-content"
    enable-search-suggestions
    :search-suggestions="searchSuggestions"
    :search-suggestions-loading="searchSuggestionsLoading"
    :search-suggestions-error="searchSuggestionsError"
    @search="handleSearch"
    @search-focus="handleSearchFocus"
    @filter="showMobileFilter = true"
    @suggestion-select="handleSuggestionSelect"
    @suggestion-retry="retrySearchSuggestions"
  >
    <template #sidebar>
      <div class="download-sidebar__filter-wrapper">
        <BaseFilter
          v-model="activeFilters"
          v-model:visible="showMobileFilter"
          :groups="visibleFilterGroups"
          plain-desktop
          compact
          :mobile-placement="isMobile ? 'bottom' : 'right'"
        />
      </div>
    </template>

    <div v-if="loading" class="download-content__loading">
      <a-skeleton :animation="true" role="status" aria-label="下载内容加载中">
        <a-skeleton-line :rows="1" :widths="['72px']" :line-height="20" />
        <a-skeleton-line :rows="5" :line-height="28" />
      </a-skeleton>
    </div>
    <div v-else-if="listError" class="download-content__error" role="alert">
      <p>下载内容加载失败，请稍后重试</p>
      <a-button @click="fetchDownloadList()">重试</a-button>
    </div>
    <template v-else>
      <ContentListHeader :total="total" class="download-content__header" />

      <div class="download-list">
        <BaseDownloadFileItem
          v-for="item in downloadList"
          :key="item.contentId"
          :title="item.displayName"
          :icon-src="withBase(getFileIconPath(item.fileUrl?.split('.').pop() || 'pdf'))"
          :date-text="formatDownloadTime(item)"
          :download-count="!isVideoFile(item) ? item.downloadCount || 0 : ''"
          :preview-count="isVideoFile(item) ? item.previewCount || 0 : ''"
          :file-size="item.fileSize ? formatFileSize(item.fileSize, { compactKbMb: isMobile }) : ''"
          :can-preview="Boolean(getDownloadPreviewUrl(item))"
          :can-download="Boolean(item.downloadUrl) && !isVideoFile(item)"
          :downloading="isItemDownloading(item)"
          :share-url="getShareUrl(item)"
          main-action="preview"
          desktop-action-mode="buttons"
          @preview="handleViewItem(item)"
          @download="handleDownload(item)"
          @shared="handleTrackAction(item, 3)"
        >
          <template #title>
            <span v-safe-html="highlightText(item.displayName)"></span>
          </template>
        </BaseDownloadFileItem>

        <!-- 空状态 -->
        <div v-if="downloadList.length === 0" class="download-empty">
          <img
            :src="withBase('/images/icons/empty/illustration_no_search.svg')"
            alt="暂无搜索结果"
            class="download-empty__image"
          />
          <p class="download-empty__text">很遗憾，没有搜索到相关文件~</p>
        </div>
      </div>

      <!-- 分页 -->
      <div v-if="total > pageSize" class="download-pagination">
        <BaseSimplePagination
          v-model:current="currentPage"
          :total="totalPages"
          @change="handlePageChange"
        />
      </div>
    </template>
  </ServiceListPageLayout>
</template>

<style scoped lang="scss">
.download-sidebar__filter-wrapper {
  width: 100%;
  box-sizing: border-box;

  @include respond-to('lg') {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  :deep(.base-filter) {
    @include respond-to('lg') {
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  }

  :deep(.base-filter__desktop) {
    @include respond-to('lg') {
      max-height: calc(100dvh - #{$base-header-height} - 48px);
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
  }

  :deep(.base-filter__content) {
    @include respond-to('lg') {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
    }
  }
}

:deep(.download-content) {
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  height: 100%;
  border-radius: 12px;
  background: $bg-white;
  padding: 24px 32px;
  box-sizing: border-box;

  @include respond-to-max('lg') {
    margin: 0;
    padding: 20px 16px;
    border-radius: 12px;
  }

  .download-content__header {
    margin-bottom: 24px;

    @include respond-to-max('lg') {
      margin-bottom: 16px;
      padding: 0;
    }
  }

  .download-content__loading {
    padding: 24px 0;
    min-height: 400px;
  }

  .download-content__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    min-height: 400px;
    text-align: center;
  }

  .download-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
    min-height: 400px;

    @include respond-to-max('lg') {
      padding: 40px 0;
      min-height: 300px;
    }

    &__image {
      width: 200px;
      height: auto;
      margin-bottom: 24px;
      object-fit: contain;

      @include respond-to-max('lg') {
        width: 160px;
        margin-bottom: 20px;
      }
    }

    &__text {
      margin: 0;
      color: $text-subtitle-color;
      font-size: $font-size-card-content;
      line-height: 1.5;

      @include respond-to-max('lg') {
        font-size: 13px;
      }
    }
  }
}

.download-list {
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
  gap: 24px;
}

.download-pagination {
  margin-top: 4px;
  display: flex;
  justify-content: center;
}

@include respond-to-max('lg') {
  :deep(.download-content) {
    width: 100%;
    margin: 0;
    padding: 12px 16px;
    border-radius: 12px;
    box-sizing: border-box;

    .download-content__header {
      gap: 8px;
      align-items: flex-start;
      margin-bottom: 12px;
      padding: 4px 0;

      :deep(.content-list-header__left) {
        flex-shrink: 0;
      }

      :deep(.content-list-header__count) {
        display: inline-block;
        font-size: 12px;
        line-height: 18px;
        white-space: nowrap;
      }

      :deep(.content-list-header__help-wrapper) {
        flex: 1;
        justify-content: flex-end;
        min-width: 0;
        height: 22px;
      }

      :deep(.content-list-header__help-link) {
        align-items: center;
        gap: 4px;
        height: 22px;
        font-size: 12px;
        line-height: 18px;
      }

      :deep(.content-list-header__help-text) {
        font-size: 12px;
        line-height: 18px;
      }
    }
  }

  .download-list {
    gap: 12px;
    margin-bottom: 12px;
  }

  .download-pagination {
    margin-top: 0;
  }
}
</style>

<style lang="scss">
@include respond-to-max('lg') {
  .download-center-page {
    .service-search-hero__mobile-title {
      padding-top: 12px;
      font-size: 20px;
      font-weight: 500;
      line-height: 28px;
    }

    .service-search-hero__mobile-row {
      gap: 12px;
      height: 60px;
      padding: 12px 16px;
    }

    .service-search-hero__mobile-search {
      height: 36px;
      border-radius: $radius-6;
    }

    .service-search-hero__filter--active {
      color: $primary-color;

      .service-search-hero__filter-icon {
        color: $primary-color;
      }
    }
  }
}
</style>
